import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FilterConfig, useGenericFilter } from 'hooks/useGenericFilter';

interface GenericFilterProps {
  config: FilterConfig;
  onFilterChange?: (filters: Record<string, string[]>) => void;
  className?: string;
  filterTriggerClassName?: string;
  activeFilterClassName?: string;
}

export const GenericFilter: React.FC<GenericFilterProps> = ({
  config,
  onFilterChange,
  className = "",
  filterTriggerClassName = "",
  activeFilterClassName = ""
}) => {
  const {
    activeFilters,
    filterValues,
    addActiveFilter,
    removeActiveFilter,
    toggleFilterValue,
  } = useGenericFilter({ config, onFilterChange });

  const handleClearFilter = (e: React.MouseEvent, filterKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    removeActiveFilter(filterKey);
  };

  return (
    <div className={`flex gap-5 items-center ${className}`}>
      {/* Active Filter Pills */}
      {activeFilters.map((filterKey) => {

        return (
          <div key={filterKey} className="relative">
            {/* Clear button positioned outside the dropdown trigger */}
            <button
              onClick={(e) => handleClearFilter(e, filterKey)}
              className="absolute right-0 -top-2 z-20 bg-white rounded-full"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 16C3.58698 16 0 12.413 0 8C0 3.58698 3.58698 0 8 0C12.413 0 16 3.58698 16 8C16 12.413 12.413 16 8 16ZM8 1.11628C4.20465 1.11628 1.11628 4.20465 1.11628 8C1.11628 11.7953 4.20465 14.8837 8 14.8837C11.7953 14.8837 14.8837 11.7953 14.8837 8C14.8837 4.20465 11.7953 1.11628 8 1.11628Z"
                  fill="#165FE3"
                />
                <path
                  d="M5.89222 10.6624C5.75082 10.6624 5.60943 10.6104 5.4978 10.4987C5.28198 10.2829 5.28198 9.92571 5.4978 9.70989L9.70989 5.4978C9.9257 5.28198 10.2829 5.28198 10.4987 5.4978C10.7145 5.71361 10.7145 6.07082 10.4987 6.28663L6.28663 10.4987C6.18245 10.6104 6.03361 10.6624 5.89222 10.6624Z"
                  fill="#165FE3"
                />
                <path
                  d="M10.1043 10.6624C9.96291 10.6624 9.82152 10.6104 9.70989 10.4987L5.4978 6.28663C5.28198 6.07082 5.28198 5.71361 5.4978 5.4978C5.71361 5.28198 6.07082 5.28198 6.28663 5.4978L10.4987 9.70989C10.7145 9.92571 10.7145 10.2829 10.4987 10.4987C10.3871 10.6104 10.2457 10.6624 10.1043 10.6624Z"
                  fill="#165FE3"
                />
              </svg>
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className={`py-2 px-3.5 pr-6 flex whitespace-nowrap items-center gap-2 bg-[#F9F9F9] rounded-lg text-sm font-medium text-black-label ${activeFilterClassName}`}
                >
                  {config[filterKey]?.label || filterKey}                                                     
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 13 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.6181 4.19035L6.81156 7.93679C6.72851 8.01895 6.61639 8.06504 6.49956 8.06504C6.38273 8.06504 6.27062 8.01895 6.18756 7.93679L2.381 4.19116C2.29743 4.10904 2.18495 4.06302 2.06778 4.06302C1.95061 4.06302 1.83813 4.10904 1.75456 4.19116C1.71341 4.23128 1.6807 4.27923 1.65837 4.33219C1.63604 4.38514 1.62454 4.44203 1.62454 4.49951C1.62454 4.55698 1.63604 4.61387 1.65837 4.66682C1.6807 4.71978 1.71341 4.76773 1.75456 4.80785L5.56031 8.55348C5.81098 8.7996 6.14825 8.9375 6.49956 8.9375C6.85087 8.9375 7.18814 8.7996 7.43881 8.55348L11.2446 4.80785C11.2858 4.76772 11.3186 4.71972 11.3411 4.66669C11.3635 4.61366 11.375 4.55667 11.375 4.4991C11.375 4.44153 11.3635 4.38455 11.3411 4.33151C11.3186 4.27848 11.2858 4.23048 11.2446 4.19035C11.161 4.10823 11.0485 4.06221 10.9313 4.06221C10.8142 4.06221 10.7017 4.10823 10.6181 4.19035Z"
                      fill="#333333"
                    />
                  </svg>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <FilterDropdownContent
                  filterKey={filterKey}
                  config={config[filterKey]}
                  selectedValues={filterValues[filterKey] || []}
                  onToggleValue={toggleFilterValue}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      })}

      {/* Filter Trigger Button */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={`${filterTriggerClassName}`}>
            <svg
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.9384 23.1516C10.4584 23.1516 9.98844 23.0316 9.54844 22.7916C8.66844 22.3016 8.13844 21.4116 8.13844 20.4116V15.1116C8.13844 14.6116 7.80844 13.8616 7.49844 13.4816L3.75844 9.52156C3.12844 8.89156 2.64844 7.81156 2.64844 7.00156V4.70156C2.64844 3.10156 3.85844 1.85156 5.39844 1.85156H18.5984C20.1184 1.85156 21.3484 3.08156 21.3484 4.60156V6.80156C21.3484 7.85156 20.7184 9.04156 20.1284 9.63156L15.7984 13.4616C15.3784 13.8116 15.0484 14.5816 15.0484 15.2016V19.5016C15.0484 20.3916 14.4884 21.4216 13.7884 21.8416L12.4084 22.7316C11.9584 23.0116 11.4484 23.1516 10.9384 23.1516ZM5.39844 3.35156C4.69844 3.35156 4.14844 3.94156 4.14844 4.70156V7.00156C4.14844 7.37156 4.44844 8.09156 4.82844 8.47156L8.63844 12.4816C9.14844 13.1116 9.64844 14.1616 9.64844 15.1016V20.4016C9.64844 21.0516 10.0984 21.3716 10.2884 21.4716C10.7084 21.7016 11.2184 21.7016 11.6084 21.4616L12.9984 20.5716C13.2784 20.4016 13.5584 19.8616 13.5584 19.5016V15.2016C13.5584 14.1316 14.0784 12.9516 14.8284 12.3216L19.1084 8.53156C19.4484 8.19156 19.8584 7.38156 19.8584 6.79156V4.60156C19.8584 3.91156 19.2984 3.35156 18.6084 3.35156H5.39844Z"
                fill="#165FE3"
              />
              <path
                d="M6.00358 11.2473C5.86358 11.2473 5.73358 11.2073 5.60358 11.1373C5.25358 10.9173 5.14358 10.4473 5.36358 10.0973L10.2936 2.19733C10.5136 1.84733 10.9736 1.73733 11.3236 1.95733C11.6736 2.17733 11.7836 2.63733 11.5636 2.98733L6.63358 10.8873C6.49358 11.1173 6.25358 11.2473 6.00358 11.2473Z"
                fill="#165FE3"
              />
            </svg>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          {Object.entries(config).map(([filterKey, filterGroup]) => (
            <DropdownMenuCheckboxItem
              key={filterKey}
              checked={activeFilters.includes(filterKey) || 'indeterminate'}
              onCheckedChange={(val) => {
                if (val) {
                  addActiveFilter(filterKey);
                } else {
                  removeActiveFilter(filterKey);
                }
              }}
              className="py-3.5"
            >
              {filterGroup.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// Helper component for rendering dropdown content
interface FilterDropdownContentProps {
  filterKey: string;
  config: any;
  selectedValues: string[];
  onToggleValue: (filterKey: string, value: string) => void;
}

const FilterDropdownContent: React.FC<FilterDropdownContentProps> = ({
  filterKey,
  config,
  selectedValues,
  onToggleValue,
}) => {
  if (config.nested && config.nestedGroups) {
    return (
      <Accordion className="w-full" type="single" collapsible>
        {Object.entries(config.nestedGroups).map(([groupKey, options]) => (
          <AccordionItem
            key={groupKey}
            className="border-none -m-1"
            value={groupKey}
          >
            <AccordionTrigger className="capitalize hover:no-underline [&[data-state=open]]:bg-[#E1EBFC] py-3.5 px-5">
              {groupKey}
            </AccordionTrigger>
            <AccordionContent>
              {(options as any[]).map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value}
                  checked={selectedValues.includes(option.value)}
                  onCheckedChange={() => {
                    onToggleValue(filterKey, option.value);
                  }}
                  className="py-3.5"
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    );
  }

  return (
    <>
      {config.options?.map((option: any) => (
        <DropdownMenuCheckboxItem
          key={option.value}
          checked={selectedValues.includes(option.value) || 'indeterminate'}
          onCheckedChange={() => {
            onToggleValue(filterKey, option.value);
          }}
          className="py-3.5"
        >
          {option.label}
        </DropdownMenuCheckboxItem>
      ))}
    </>
  );
};

export default GenericFilter;